import {
  Event,
  IntervalDefinition,
  IntervalDefinitionFreqTypeEnum,
  RecurrenceRules,
} from "@clockwise/schema";
import { DateTime } from "luxon";
import { SetRequired } from "../types";
import { getValue } from "./errorable.util";
import { EFlex } from "./event-tag";
import {
  getAbsoluteDateForEvent,
  getAbsoluteDateTimeForEvent,
  getAbsoluteTimeForEvent,
} from "./format-time";
import { logger } from "./logger";
import { prettyList } from "./text";

export const DAY_STRINGS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const DAILY_ON_WEEKDAYS_STRING = "Repeats every week on weekdays";

/////////////
// Helpers //
/////////////

function validateFragmentForString(servicesRules: RecurrenceRules) {
  const isValid =
    !!servicesRules &&
    !!servicesRules.interval &&
    !!servicesRules.interval.freqType &&
    typeof servicesRules.interval.byDay !== "undefined" &&
    typeof servicesRules.interval.interval === "number";

  return isValid;
}

function getDefaultFlexibilityString(
  flex: EFlex,
  event?: SetRequired<Partial<Event>, "startTime" | "endTime">,
) {
  const day = getAbsoluteDateForEvent(event, true);
  switch (flex) {
    default:
    case EFlex.Day:
      return day ? day : "Within the day";
    case EFlex.Week:
      return day ? `Within the week of ${day}` : "Within the week";
    case EFlex.Pin:
      return getPinFlexString();
  }
}

function getWeekFlexString(recurrence: Recurrence) {
  const frequencyInterval = recurrence.freqInterval;
  const intervalFrequency = frequencyInterval > 1 ? ` ${frequencyInterval}` : "";
  const plural = frequencyInterval > 1 ? "s" : "";

  switch (recurrence.freqType) {
    default:
    case "Daily":
      // Daily recurrence shouldn't have week flex setting, but if it does, return repeats every week.
      return "Repeats every week";
    case "Weekly":
      return `Repeats every${intervalFrequency} week${plural}`;
    case "Monthly":
      return `Repeats every${intervalFrequency} month${plural} within the event's week`;
    case "Yearly":
      return `Repeats every${intervalFrequency} year${plural} within the event's week`;
  }
}

////////////////////
// STRING CREATION
////////////////////

export function getPinFlexString() {
  return `Clockwise will not optimize the time of this event`;
}

export function getFlexibilityString(
  recurrence: Recurrence | null,
  flex: EFlex,
  event?: SetRequired<Partial<Event>, "startTime" | "endTime">,
) {
  if (!recurrence) {
    return getDefaultFlexibilityString(flex, event);
  }

  switch (flex) {
    case EFlex.Week:
      return getWeekFlexString(recurrence);
    case EFlex.Day:
      return recurrence.baseRecurrenceString;
    case EFlex.Pin:
    default:
      return getPinFlexString();
  }
}

export function isRecurring(event: SetRequired<Partial<Event>, "recurrenceRules">) {
  return !!event.recurrenceRules && !!event.recurrenceRules.interval;
}

export function getEventTime(
  event: SetRequired<Partial<Event>, "recurrenceRules" | "isAllDay" | "startTime" | "endTime">,
  timezone: string,
) {
  const recurrence = isRecurring(event)
    ? new Recurrence(event.recurrenceRules as RecurrenceRules)
    : null;

  const absoluteEventTime = getAbsoluteTimeForEvent(event, timezone);
  const eventTime = !event.isAllDay && absoluteEventTime ? ` from ${absoluteEventTime}` : "";

  return recurrence
    ? `${recurrence.baseRecurrenceString}${eventTime}`
    : getAbsoluteDateTimeForEvent(event);
}

export type AutopilotTimeEvent = SetRequired<
  Partial<Event>,
  "recurrenceRules" | "startTime" | "endTime" | "isHold"
>;
export function getAutopilotTime(event: AutopilotTimeEvent, flex: EFlex) {
  const recurrence = isRecurring(event)
    ? new Recurrence(event.recurrenceRules as RecurrenceRules)
    : null;

  let time = getFlexibilityString(recurrence, flex, event);
  let attendeeString = event.isHold ? "you" : "attendees";
  if (flex !== EFlex.Pin) {
    time += ` at the best time for ${attendeeString}`;
  }

  return time;
}

export class Recurrence {
  private intervalDef: IntervalDefinition;

  constructor(rules: RecurrenceRules) {
    validateFragmentForString(rules);
    this.intervalDef = rules.interval as IntervalDefinition;
  }

  private byDayCount() {
    const { byDay } = this.intervalDef;
    if (!byDay) {
      return 0;
    }
    return byDay.split(",").length;
  }

  get freqType() {
    if (!this.intervalDef) {
      console.warn("intervalDef is null, falling back to weekly, errors likely");
      return "Weekly";
    }

    switch (this.intervalDef.freqType) {
      case "Daily":
        return "Daily";
      case "Weekly":
        return "Weekly";
      case "Monthly":
        return "Monthly";
      case "Yearly":
        return "Yearly";
      default:
        console.warn("passed unknown frequency type, falling back to weekly, errors likely");
        return "Weekly";
    }
  }

  get freqInterval() {
    if (!this.intervalDef) {
      console.warn("intervalDef is null, falling back interval of 1, errors likely");
      return 1;
    }
    return this.intervalDef.interval;
  }

  get days() {
    const { byDay } = this.intervalDef;
    if (!byDay) {
      return [];
    }
    return byDay.split(",").map((day: string) => {
      // TODO: this ignores byDay rules like '2FR'
      if (day.indexOf("SU") > -1) {
        return DAY_STRINGS[0];
      } else if (day.indexOf("MO") > -1) {
        return DAY_STRINGS[1];
      } else if (day.indexOf("TU") > -1) {
        return DAY_STRINGS[2];
      } else if (day.indexOf("WE") > -1) {
        return DAY_STRINGS[3];
      } else if (day.indexOf("TH") > -1) {
        return DAY_STRINGS[4];
      } else if (day.indexOf("FR") > -1) {
        return DAY_STRINGS[5];
      } else if (day.indexOf("SA") > -1) {
        return DAY_STRINGS[6];
      } else {
        console.warn("passed unknown byDay string; errors likely");
        return "";
      }
    });
  }

  get daysString() {
    const days = this.days;
    return days && days.length ? prettyList(days) : null;
  }

  get isOncePerDay() {
    return this.freqType === "Daily" || (this.freqType === "Weekly" && this.byDayCount() >= 5);
  }

  get isMultipleTimesPerWeek() {
    return this.freqType === "Daily" || (this.freqType === "Weekly" && this.byDayCount() > 1);
  }

  get isOncePerWeek() {
    return this.freqType === "Weekly" && this.byDayCount() === 1 && this.intervalDef.interval === 1;
  }

  get isBiWeekly() {
    return this.freqType === "Weekly" && this.intervalDef.interval === 2;
  }

  get isOncePerMonth() {
    return this.freqType === "Monthly";
  }

  get isBiMonthly() {
    return this.freqType === "Monthly" && this.intervalDef.interval === 2;
  }

  get isOncePerYear() {
    return this.freqType === "Yearly";
  }

  get isEveryDay() {
    return DAY_STRINGS.reduce((prev, curr) => prev && this.days.includes(curr), true);
  }

  get isEveryWeekDay() {
    const noSundaySaturday =
      !this.days.includes(DAY_STRINGS[0]) && !this.days.includes(DAY_STRINGS[6]);
    return (
      noSundaySaturday &&
      DAY_STRINGS.slice(1, 5).reduce((prev, curr) => prev && this.days.includes(curr), true)
    );
  }

  get freqString() {
    switch (this.freqType) {
      case "Daily":
        return "day";
      default:
      case "Weekly":
        return "week";
      case "Monthly":
        return "month";
      case "Yearly":
        return "year";
    }
  }

  get baseRecurrenceString() {
    // for every n weeks or days
    const intervalFrequency = this.intervalDef.interval > 1 ? ` ${this.intervalDef.interval}` : "";
    const plural = this.intervalDef.interval > 1 ? "s" : "";
    let maybeIncludeDay = "";

    // return a simpler string given some understanding of the event
    if (this.isEveryDay) {
      maybeIncludeDay = " on all days";
    } else if (this.isEveryWeekDay) {
      maybeIncludeDay = " on weekdays";
    } else if (this.daysString) {
      maybeIncludeDay = ` on ${this.daysString}`;
    }

    return `Repeats every${intervalFrequency} ${this.freqString}${plural}${maybeIncludeDay}`;
  }
}

export type RecurrenceRulesForName = {
  __typename: "RecurrenceRules";
  interval: {
    __typename: "IntervalDefinition";
    byDay: string | null;
    interval: number;
    freqType?: IntervalDefinitionFreqTypeEnum;
    count: number | null;
    untilTime: {
      dateOrDateTime: string;
    } | null;
  } | null;
} | null;

// The logic below is copied from the class above
// However, i didnt want to generate a whole other "Recurrence" instance that requires 5x the amount of imformation,
// So i just grabbed what was needed to create a custom recurrence string
export const getDays = (byDay: string | null) => {
  if (!byDay) {
    return [];
  }
  return byDay.split(",").map((day: string) => {
    // TODO: this ignores byDay rules like '2FR'
    if (day.indexOf("SU") > -1) {
      return DAY_STRINGS[0];
    } else if (day.indexOf("MO") > -1) {
      return DAY_STRINGS[1];
    } else if (day.indexOf("TU") > -1) {
      return DAY_STRINGS[2];
    } else if (day.indexOf("WE") > -1) {
      return DAY_STRINGS[3];
    } else if (day.indexOf("TH") > -1) {
      return DAY_STRINGS[4];
    } else if (day.indexOf("FR") > -1) {
      return DAY_STRINGS[5];
    } else if (day.indexOf("SA") > -1) {
      return DAY_STRINGS[6];
    } else {
      console.warn("passed unknown byDay string; errors likely");
      return "";
    }
  });
};

export const getFreqString = (freqType: IntervalDefinitionFreqTypeEnum | undefined) => {
  if (!freqType) {
    logger.error("PLANNER EVENT, NO FREQ TYPE");
    return "";
  }
  switch (freqType) {
    case "Daily":
      return "day";
    default:
    case "Weekly":
      return "week";
    case "Monthly":
      return "month";
    case "Yearly":
      return "year";
  }
};

export const daysString = (days: string[]) => {
  return days.length ? prettyList(days) : "";
};

export const isEveryWeekDay = (days: string[]) => {
  const noSundaySaturday = !days.includes(DAY_STRINGS[0]) && !days.includes(DAY_STRINGS[6]);
  return (
    noSundaySaturday &&
    DAY_STRINGS.slice(1, 6).reduce((prev, curr) => prev && days.includes(curr), true)
  );
};

export const isEveryDay = (days: string[]) => {
  return DAY_STRINGS.reduce((prev, curr) => prev && days.includes(curr), true);
};

export const addCustomStringToEventRecurrence = (recurrenceRules: RecurrenceRulesForName) => {
  const intervalFromRules = getValue(recurrenceRules?.interval);
  if (!intervalFromRules) {
    logger.error("PLANNER EVENT, Recurrence rules are invalid for string generation", {
      recurrenceRules: recurrenceRules,
    });
    return "Repeating Event";
  }
  const byDay = intervalFromRules.byDay;
  const interval = intervalFromRules.interval;
  const freqType = intervalFromRules.freqType;

  const days = getDays(byDay);

  const intervalFrequency = interval > 1 ? ` ${interval}` : "";
  const plural = interval > 1 ? "s" : "";
  let maybeIncludeDay = "";

  if (interval === 1 && isEveryWeekDay(days) && freqType === "Weekly") {
    return DAILY_ON_WEEKDAYS_STRING;
  }

  let untilString = "";
  if (intervalFromRules?.untilTime?.dateOrDateTime) {
    untilString = ` until ${DateTime.fromISO(intervalFromRules?.untilTime?.dateOrDateTime).toFormat(
      "LLL dd yyyy",
    )}`;
  } else if (intervalFromRules?.count) {
    untilString = `, ${intervalFromRules?.count} times`;
  }

  // return a simpler string given some understanding of the event
  if (isEveryDay(days)) {
    maybeIncludeDay = " on all days";
  } else if (isEveryWeekDay(days)) {
    maybeIncludeDay = " on weekdays";
  } else if (daysString(days)) {
    maybeIncludeDay = ` on ${daysString(days)}`;
  }

  return `Repeats every${intervalFrequency} ${getFreqString(
    freqType,
  )}${plural}${maybeIncludeDay}${untilString}`;
};
